import { useNavigate, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Avatar, Badge, Button } from "@/design-system";
import { Icon } from "@/components/Icon";
import { ApiRequestError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  reservationError,
  useBookLesson,
  useCancelLesson,
  useLesson,
  useMyCredits,
} from "@/api/queries";
import { formatDays, formatDuration, formatTime, formatTimeAndDuration } from "@/lib/format";
import { isBookingsClosed, isBookingsNotOpen } from "@/lib/booking";
import { instructorLabel } from "@/lib/instructor";
import { useOnline } from "@/lib/useOnline";
import { OfflineErrorState } from "@/components/OfflineErrorState";
import screen from "@/layout/screen.module.css";

/**
 * Human copy for a failed booking. `already_booked` is a *truthful*
 * state — the mutation keeps the optimistic "booked" state — so it is
 * not an error to surface.
 */
function reservationErrorMessage(err: ApiRequestError, t: TFunction): string | null {
  switch (reservationError(err)) {
    case "already_booked":
      return null;
    case "full":
      return t("classDetail.fullWaitlist");
    case "too_late":
      return t("classDetail.tooLate");
    case "too_early":
      return t("classDetail.tooEarly");
    default:
      return err.message || t("classDetail.bookingFailed");
  }
}

/**
 * Class detail — hero gradient, coach row, meta grid, sticky book bar.
 *
 * Reads the lesson directly via `GET /api/lessons/:id` (issue #273), so a
 * deep link to a lesson outside the schedule's 14-day window (or in the
 * past) still resolves. Booking/cancelling still updates the shared
 * `useBookLesson` / `useCancelLesson` caches optimistically (issue #271),
 * so the calendar and "My reservations" stay fresh without a manual
 * refresh.
 */
export function ClassDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const online = useOnline();
  // The student's own sanitized balance (issue #249). Falls back to the
  // value embedded on `GET /api/me` until the query resolves.
  const creditsQuery = useMyCredits(token);
  const balance = creditsQuery.data?.balance ?? user?.credit_balance;

  // Single-lesson read (issue #273): no date window, so a deep link to a
  // past lesson or one more than 14 days out still resolves instead of
  // falling through to the "no longer on the schedule" state.
  const lessonId = id ? Number(id) : NaN;
  const lessonQuery = useLesson(token, Number.isFinite(lessonId) ? lessonId : null);
  const lesson = lessonQuery.data ?? null;

  const bookMutation = useBookLesson(token);
  const cancelMutation = useCancelLesson(token);
  const loading = lessonQuery.isLoading;
  const pending = bookMutation.isPending || cancelMutation.isPending;

  function book() {
    if (lesson) bookMutation.mutate(lesson.id);
  }

  function cancel() {
    if (lesson) cancelMutation.mutate(lesson.id);
  }

  const bookingError = bookMutation.error
    ? reservationErrorMessage(bookMutation.error, t)
    : cancelMutation.error
      ? cancelMutation.error.message || t("classDetail.cancellationFailed")
      : null;
  const error = lessonQuery.isError
    ? lessonQuery.error.status === 404
      ? t("classDetail.notFound")
      : (lessonQuery.error.message ?? t("classDetail.loadFailed"))
    : bookingError;

  if (loading) {
    return (
      <div style={{ padding: "var(--space-8)", textAlign: "center", color: "var(--text-faint)" }}>
        {t("common.loading")}
      </div>
    );
  }

  if (error || !lesson) {
    return (
      <div style={{ padding: "var(--space-6)" }}>
        <button
          type="button"
          onClick={() => navigate(-1)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            background: "none",
            border: "none",
            cursor: "pointer",
            color: "var(--brand)",
            font: "var(--fw-semibold) var(--fs-sm)/1 var(--font-body)",
            padding: 0,
            marginBottom: "var(--space-4)",
          }}
        >
          <Icon name="arrow-left" size={18} /> {t("common.back")}
        </button>
        {!online ? (
          <OfflineErrorState />
        ) : (
          <div
            role="alert"
            style={{
              background: "var(--status-danger-bg)",
              color: "var(--status-danger-fg)",
              padding: "var(--space-4)",
              borderRadius: "var(--radius-md)",
              font: "var(--fw-medium) var(--fs-sm)/1.4 var(--font-body)",
            }}
          >
            {error ?? t("classDetail.notFound")}
          </div>
        )}
      </div>
    );
  }

  const isBooked = lesson.reserved_by_me;
  const isFull = lesson.spots_left <= 0 && !isBooked;
  const bookingsClosed = isBookingsClosed(lesson, new Date());
  const bookingsNotOpen = isBookingsNotOpen(lesson, new Date());
  // An open seat costs a credit: once the balance is known and zero, block
  // the CTA and explain why (issue #249). While the balance is still
  // loading we let the server be the judge.
  const noCredits =
    lesson.lesson_type === "open" &&
    !isBooked &&
    balance !== undefined &&
    balance <= 0;

  const meta = [
    { icon: "clock" as const, label: formatTimeAndDuration(lesson.starts_at, lesson.duration_minutes) },
    {
      icon: "users" as const,
      label: isFull
        ? t("common.classFull")
        : t("classDetail.spotsLeft", { count: lesson.spots_left }),
    },
  ];

  return (
    <div className={screen.capped} style={{ paddingBottom: 110 }}>
      {/* hero */}
      <div
        style={{
          position: "relative",
          height: 240,
          background: "var(--terracotta-700)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "linear-gradient(to top, rgba(33,26,20,0.7), transparent 55%)",
          }}
        />
        <button
          type="button"
          onClick={() => navigate(-1)}
          aria-label={t("common.back")}
          style={{
            position: "absolute",
            top: 18,
            left: 18,
            width: 40,
            height: 40,
            borderRadius: 999,
            border: "none",
            background: "rgba(255,255,255,0.92)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            boxShadow: "var(--shadow-sm)",
            color: "var(--text-strong)",
          }}
        >
          <Icon name="arrow-left" size={20} />
        </button>
        <div style={{ position: "absolute", left: 20, bottom: 18, right: 20 }}>
          <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            <Badge tone="brand" variant="solid">
              {formatTime(lesson.starts_at)}
            </Badge>
          </div>
          <div
            style={{
              font: "var(--fw-extra) var(--fs-3xl)/1.1 var(--font-display)",
              color: "#fff",
              letterSpacing: "var(--ls-tight)",
            }}
          >
            {lesson.title}
          </div>
        </div>
      </div>

      {/* body */}
      <div
        style={{
          padding: 20,
          display: "flex",
          flexDirection: "column",
          gap: 20,
        }}
      >
        {/* coach */}
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Avatar name={instructorLabel(lesson)} size={48} ring />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              data-testid="class-detail-instructor"
              style={{
                font: "var(--fw-bold) var(--fs-md)/1.1 var(--font-display)",
                color: "var(--text-strong)",
              }}
            >
              {instructorLabel(lesson)}
            </div>
          </div>
        </div>

        {/* meta grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 10,
          }}
        >
          {meta.map((m) => (
            <div
              key={m.label}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                background: "var(--surface-muted)",
                borderRadius: "var(--radius-md)",
                padding: "12px 14px",
              }}
            >
              <Icon name={m.icon} size={18} color="var(--brand)" />
              <span
                style={{
                  font: "var(--fw-medium) var(--fs-xs)/1.2 var(--font-body)",
                  color: "var(--text-body)",
                }}
              >
                {m.label}
              </span>
            </div>
          ))}
        </div>

        {/* about */}
        <div>
          <div
            style={{
              font: "var(--fw-bold) var(--fs-md)/1 var(--font-display)",
              color: "var(--text-strong)",
              marginBottom: 8,
            }}
          >
            {t("classDetail.aboutClass")}
          </div>
          <p
            style={{
              font: "var(--fw-regular) var(--fs-md)/1.6 var(--font-body)",
              color: "var(--text-muted)",
              margin: 0,
            }}
          >
            {lesson.description?.trim() ||
              t("classDetail.descriptionFallback", {
                duration: formatDuration(lesson.duration_minutes),
                capacity: lesson.capacity,
              })}
          </p>
        </div>
      </div>

      {/* sticky book bar */}
      <div
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          background: "linear-gradient(to top, var(--bg-canvas) 70%, transparent)",
          zIndex: 40,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 14,
            padding: "16px 20px 22px",
            maxWidth: "var(--container-prose)",
            margin: "0 auto",
          }}
        >
        <div>
          <div
            style={{
              font: "var(--fw-regular) var(--fs-3xs)/1 var(--font-body)",
              color: "var(--text-faint)",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
            }}
          >
            {lesson.lesson_type === "assigned"
              ? t("classDetail.membershipLabel")
              : t("classDetail.credits")}
          </div>
          <div
            style={{
              font: "var(--fw-bold) var(--fs-lg)/1 var(--font-display)",
              color: "var(--text-strong)",
              marginTop: 4,
            }}
          >
            {lesson.lesson_type === "assigned"
              ? t("classDetail.includedInMembership")
              : t("classDetail.oneClass")}
          </div>
          {lesson.lesson_type === "open" && balance !== undefined && (
            <div
              data-testid="class-detail-balance"
              style={{
                font: "var(--fw-regular) var(--fs-2xs)/1 var(--font-body)",
                color: noCredits ? "var(--status-danger-fg)" : "var(--text-faint)",
                marginTop: 4,
              }}
            >
              {t("classDetail.balanceLeft", { count: balance })}
            </div>
          )}
        </div>
        {isBooked ? (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
            <Button
              variant="soft"
              size="lg"
              fullWidth
              disabled={pending || bookingsClosed || !online}
              title={!online ? t("offline.bookingDisabled") : undefined}
              onClick={cancel}
              iconLeft={<Icon name="check" size={20} color="var(--brand-press)" />}
              data-testid={
                lesson.lesson_type === "assigned"
                  ? "class-detail-unenroll"
                  : "class-detail-cancel"
              }
            >
              {bookingsClosed
                ? t("classDetail.closed")
                : pending
                  ? t("common.cancelling")
                  : lesson.lesson_type === "assigned"
                    ? t("classDetail.enrolledByTeacher")
                    : t("classDetail.bookedSeeYou")}
            </Button>
            {lesson.lesson_type === "assigned" && !bookingsClosed && (
              <span
                data-testid="class-detail-enrolled-hint"
                style={{
                  font: "var(--fw-regular) var(--fs-2xs)/1.4 var(--font-body)",
                  color: "var(--text-faint)",
                }}
              >
                {t("classDetail.enrolledHint")}
              </span>
            )}
            {bookingsClosed && (
              <span
                data-testid="class-detail-cancel-closed"
                style={{
                  font: "var(--fw-regular) var(--fs-2xs)/1.4 var(--font-body)",
                  color: "var(--text-faint)",
                }}
              >
                {t("classDetail.cancelClosedNote", {
                  duration: formatDuration(lesson.booking_cutoff_minutes),
                })}
              </span>
            )}
          </div>
        ) : lesson.lesson_type === "assigned" ? (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
            <Button
              variant="secondary"
              size="lg"
              fullWidth
              disabled
              data-testid="class-detail-assigned"
            >
              {t("classDetail.assignedOnly")}
            </Button>
            <span
              data-testid="class-detail-assigned-note"
              style={{
                font: "var(--fw-regular) var(--fs-2xs)/1.4 var(--font-body)",
                color: "var(--text-faint)",
              }}
            >
              {t("classDetail.assignedOnlyNote")}
            </span>
          </div>
        ) : (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
            <Button
              variant="primary"
              size="lg"
              fullWidth
              disabled={
                pending || bookingsClosed || bookingsNotOpen || isFull || !online || noCredits
              }
              title={!online ? t("offline.bookingDisabled") : undefined}
              onClick={book}
              data-testid="class-detail-book"
            >
              {pending
                ? t("classDetail.booking")
                : isFull
                  ? t("common.classFull")
                  : noCredits
                    ? t("classDetail.noCredits")
                    : bookingsNotOpen
                      ? t("classDetail.notOpen")
                      : bookingsClosed
                        ? t("classDetail.closed")
                        : t("classDetail.bookThisClass")}
            </Button>
            {isFull && (
              <span
                data-testid="class-detail-book-full"
                style={{
                  font: "var(--fw-regular) var(--fs-2xs)/1.4 var(--font-body)",
                  color: "var(--text-faint)",
                }}
              >
                {t("common.waitlistSoon")}
              </span>
            )}
            {!isFull && noCredits && (
              <span
                data-testid="class-detail-no-credits"
                style={{
                  font: "var(--fw-regular) var(--fs-2xs)/1.4 var(--font-body)",
                  color: "var(--text-faint)",
                }}
              >
                {t("classDetail.noCreditsNote")}
              </span>
            )}
            {bookingsClosed && (
              <span
                data-testid="class-detail-book-closed"
                style={{
                  font: "var(--fw-regular) var(--fs-2xs)/1.4 var(--font-body)",
                  color: "var(--text-faint)",
                }}
              >
                {t("classDetail.closedNote", {
                  duration: formatDuration(lesson.booking_cutoff_minutes),
                })}
              </span>
            )}
            {bookingsNotOpen && (
              <span
                data-testid="class-detail-book-not-open"
                style={{
                  font: "var(--fw-regular) var(--fs-2xs)/1.4 var(--font-body)",
                  color: "var(--text-faint)",
                }}
              >
                {t("classDetail.notOpenNote", {
                  days: formatDays(lesson.booking_open_days ?? 0),
                })}
              </span>
            )}
          </div>
        )}
        </div>
      </div>
    </div>
  );
}
